import moment from 'moment-timezone';

export const TIME_FORMAT_12H = '12h';
export const TIME_FORMAT_24H = '24h';

// moment's time patterns for each stored preference. Mirrors the backend's
// TranslateDatePatternAction::toPhpTimeFormat() ('g:i a' / 'H:i' in PHP's vocabulary).
const TIME_PATTERNS = {
  [TIME_FORMAT_12H]: 'h:mm a',
  [TIME_FORMAT_24H]: 'HH:mm',
};

class TimezoneManager {
  // Labels depend on the display language, so cache per locale rather than rebuilding
  // ~400 zones × 2 Intl formatters on every render.
  #optionsByLocale = new Map();

  /**
   * The moment time pattern an outlet should render in: its own stored preference,
   * else the hour cycle its region declares. The region is the backend's own answer
   * (regional_configs.hour_cycle), so a location with no preference of its own looks
   * identical on both sides without this repo keeping a country table.
   */
  getTimePattern(timeFormat, hourCycle) {
    return TIME_PATTERNS[timeFormat] ?? TIME_PATTERNS[this.getDefaultTimeFormat(hourCycle)];
  }

  getDefaultTimeFormat(hourCycle) {
    return hourCycle === 'h12' ? TIME_FORMAT_12H : TIME_FORMAT_24H;
  }

  /**
   * Selectable IANA zones, labelled for humans and carrying a lower-cased `search`
   * blob so the picker can match on the zone id, the city, or the zone's name in
   * either its full ("Eastern Standard Time") or generic ("Eastern Time") form.
   */
  getOptions(displayLanguage = 'en') {
    if (this.#optionsByLocale.has(displayLanguage)) {
      return this.#optionsByLocale.get(displayLanguage);
    }

    const options = this.#zones()
      // Etc/* and the bare aliases are real zones but meaningless as a restaurant's
      // location, and they crowd out the city the operator is actually looking for.
      .filter((zone) => zone.includes('/') && !zone.startsWith('Etc/'))
      .map((zone) => {
        const city = zone.split('/').pop().replace(/_/g, ' ');
        const fullName = this.#zoneName(zone, displayLanguage, 'long');
        const genericName = this.#zoneName(zone, displayLanguage, 'longGeneric');
        const offset = moment.tz(zone).format('Z');

        return {
          value: zone,
          label: `${city} — ${genericName || fullName || zone} (GMT${offset})`,
          search: [zone, city, fullName, genericName].join(' ').toLowerCase(),
          // Kept separately so ranking can tell "this zone IS Eastern Time" from
          // "this zone's name merely contains those words".
          city,
          genericName: genericName || fullName || zone,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label, displayLanguage));

    this.#optionsByLocale.set(displayLanguage, options);

    return options;
  }

  /**
   * Every whitespace-separated token must appear somewhere in the option's search
   * blob. Substring matching alone would fail the obvious query: "eastern time" is
   * not a substring of "eastern standard time".
   */
  matchesOption(input, option) {
    const tokens = (input ?? '').toLowerCase().split(/\s+/).filter(Boolean);

    if (!tokens.length) {
      return true;
    }

    return tokens.every((token) => option.search.includes(token));
  }

  /**
   * How well an option answers the query — lower sorts first. Alphabetical order alone
   * buries the obvious answer: "Eastern Time" matches "Eastern European Time" (Athens,
   * Bucharest, Cairo) and "Australian Eastern Time" (Brisbane, Canberra) too, all of
   * which sort ahead of New York by city name.
   */
  scoreOption(input, option) {
    const query = (input ?? '').trim().toLowerCase();

    if (!query) {
      return 2;
    }

    const name = option.genericName.toLowerCase();

    // The zone the query names outright, or the city being typed.
    if (name === query || option.city.toLowerCase().startsWith(query)) {
      return 0;
    }

    if (name.includes(query)) {
      return 1;
    }

    return 2;
  }

  /** Comparator for antd's `filterSort`: relevance first, then city name. */
  compareOptions(optionA, optionB, searchValue) {
    const byScore = this.scoreOption(searchValue, optionA) - this.scoreOption(searchValue, optionB);

    return byScore !== 0 ? byScore : optionA.label.localeCompare(optionB.label);
  }

  /**
   * Intl's canonical zone list, which moment's is not: `moment.tz.names()` also carries
   * deprecated links, so an operator searching "Eastern Time" was offered `US/Eastern`,
   * `America/Fort_Wayne` and Indianapolis twice. Falls back to moment where
   * `supportedValuesOf` is missing (pre-2022 browsers).
   */
  #zones() {
    try {
      return Intl.supportedValuesOf('timeZone');
    } catch {
      return moment.tz.names();
    }
  }

  #zoneName(zone, displayLanguage, style) {
    try {
      return (
        new Intl.DateTimeFormat(displayLanguage, { timeZone: zone, timeZoneName: style })
          .formatToParts(new Date())
          .find((part) => part.type === 'timeZoneName')?.value ?? ''
      );
    } catch {
      return '';
    }
  }
}

export default new TimezoneManager();
